import { useState } from 'react';
import { Controller, Path, useFormContext, useWatch } from 'react-hook-form';
import { Radio, RadioGroup, TmdxTextarea } from '@ds';
import { FormLinkDescriptionElement } from '@ui';
import { DocumentsFileInput } from '@features/components/CaseFormBlocks/components';
import { DOCUMENT_FILE_TYPES } from '@features/components/CaseFormBlocks/config';
import { DonorFormRow } from './DonorFormRow';
import { DonorProfileFormValues } from '../types';
import { createOrganField, OrganScopedSectionProps } from '../organField';

export const CtScanRow = ({ namePrefix }: OrganScopedSectionProps) => {
  const { control, setValue } = useFormContext<DonorProfileFormValues>();
  const organField = createOrganField(namePrefix);
  const ctScan = useWatch({ control, name: organField('ctScan') });
  const [isLinkEditing, setIsLinkEditing] = useState(false);

  // Single fixed slot, not a user-addable list — "delete" clears it back to an empty,
  // re-editable link rather than removing the row.
  // Field paths use bracket notation because FormLink's Controller registers `ctScanLink[0].url` —
  // setValue('ctScanLink', [...]) replacing the whole array doesn't notify that Controller.
  const handleDeleteLink = () => {
    setValue(organField('ctScanLink[0].url') as Path<DonorProfileFormValues>, '', { shouldDirty: true });
    setValue(organField('ctScanLink[0].description') as Path<DonorProfileFormValues>, '', { shouldDirty: true });
    setIsLinkEditing(true);
  };

  return (
    <>
      <DonorFormRow title="CT Scan">
        <Controller
          name={organField('ctScan')}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value === null || value === undefined ? '' : String(value)}
              isInvalid={invalid}
              errorMessage={error?.message}
              onValueChange={(v) => onChange(v === 'true')}
              {...field}
            >
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="true" size="lg">
                Yes
              </Radio>
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="false" size="lg">
                No
              </Radio>
            </RadioGroup>
          )}
        />
      </DonorFormRow>

      {ctScan && (
        <>
          <DonorFormRow title="CT Scan Details" multiRow>
            <Controller
              name={organField('ctScanDetails')}
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <TmdxTextarea
                  aria-label="CT scan details"
                  maxLength={512}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                  value={field.value ?? ''}
                />
              )}
            />
          </DonorFormRow>

          <DonorFormRow title="CT Scan Link" multiRow contentClass="overflow-hidden">
            <div className="flex min-w-0 w-full max-w-full flex-col gap-2 overflow-hidden">
              <FormLinkDescriptionElement
                index={0}
                formFieldName={organField('ctScanLink')}
                handleDelete={handleDeleteLink}
                isEditing={isLinkEditing}
                setEditingIndex={(idx) => setIsLinkEditing(idx !== null)}
                isDescriptionEnabled={false}
              />
            </div>
          </DonorFormRow>

          <DonorFormRow title="CT Scan Documents" multiRow>
            <Controller
              name={organField('ctScanDocuments')}
              control={control}
              render={({ field: { value, onChange, ref }, fieldState: { invalid, error } }) => (
                <DocumentsFileInput
                  value={value}
                  onChange={onChange}
                  inputRef={ref}
                  accept={DOCUMENT_FILE_TYPES}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                />
              )}
            />
          </DonorFormRow>
        </>
      )}
    </>
  );
};
